import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Box,
  Chip,
  Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Search from '../Search/Search';

const SeedSentenceSelector = ({
  open,
  onClose,
  onConfirm,
  availableSeeds,
  usedSeedIds,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const handleToggle = (seedId) => {
    setSelectedIds((prev) =>
      prev.includes(seedId) ? prev.filter((id) => id !== seedId) : [...prev, seedId]
    );
  };

  const handleSelectAll = () => {
    const filteredIds = filteredSeeds.map((seed) => seed._id);
    const allFilteredSelected = filteredIds.every((id) => selectedIds.includes(id));

    if (allFilteredSelected && filteredIds.length > 0) {
      // Deselect all filtered
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      // Select all filtered (merge with existing)
      setSelectedIds((prev) => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const handleConfirm = () => {
    if (selectedIds.length === 0) {
      return; // Must select at least one
    }
    onConfirm(selectedIds);
    setSelectedIds([]); // Reset selection
  };

  const handleCancel = () => {
    setSelectedIds([]);
    onClose();
  };

  const filteredSeeds = useMemo(() => {
    if (!searchTerm.trim()) {
      return availableSeeds;
    }

    const lowerSearch = searchTerm.toLowerCase();

    // Parse and evaluate search expression with grouping support
    const evaluateExpression = (expr, text) => {
      expr = expr.trim();

      // Handle parentheses by recursively evaluating groups
      while (expr.includes('(')) {
        // Find innermost parentheses
        const innerMatch = expr.match(/\(([^()]+)\)/);
        if (!innerMatch) break;

        const innerExpr = innerMatch[1];
        const innerResult = evaluateExpression(innerExpr, text);

        // Replace the parenthesized expression with a placeholder
        // Use unique markers that won't appear in user input
        const placeholder = innerResult ? '__TRUE__' : '__FALSE__';
        expr = expr.replace(innerMatch[0], placeholder);
      }

      // Handle OR operator (lower precedence)
      if (expr.includes(' or ')) {
        const orParts = expr.split(' or ').map((p) => p.trim());
        return orParts.some((part) => evaluateExpression(part, text));
      }

      // Handle AND operator (higher precedence)
      if (expr.includes(' and ')) {
        const andParts = expr.split(' and ').map((p) => p.trim());
        return andParts.every((part) => evaluateExpression(part, text));
      }

      // Handle placeholders from parentheses evaluation
      if (expr === '__TRUE__') return true;
      if (expr === '__FALSE__') return false;

      // Base case: simple term search
      return text.includes(expr);
    };

    return availableSeeds.filter((seed) => {
      const sentenceText = seed.sentence?.toLowerCase() || '';
      const editedText = seed.edited_sentence?.toLowerCase() || '';
      const combinedText = `${sentenceText} ${editedText}`;

      try {
        return evaluateExpression(lowerSearch, combinedText);
      } catch (error) {
        // Fallback to simple search if parsing fails
        return combinedText.includes(lowerSearch);
      }
    });
  }, [availableSeeds, searchTerm]);

  const unusedSeeds = useMemo(
    () => availableSeeds.filter((seed) => !usedSeedIds.has(seed._id)),
    [availableSeeds, usedSeedIds]
  );

  const usedCount = availableSeeds.length - unusedSeeds.length;

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Select Seed Sentences</Typography>
          <Box display="flex" gap={1}>
            <Chip
              label={`${selectedIds.length} selected`}
              color={selectedIds.length > 0 ? 'primary' : 'default'}
              size="small"
            />
            <Chip
              label={`${usedCount} already used`}
              color="default"
              size="small"
              icon={<CheckCircleIcon />}
            />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', p: 2, overflow: 'hidden' }}
      >
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            Select one or more seed sentences to use for the next similarity search. Seeds
            marked with ✓ have already been used in previous searches.
          </Typography>
        </Box>

        <Box mb={2}>
          <Search
            id="seed-sentence-search"
            placeholder="Search sentences... (use AND/OR with grouping: (contract AND termination) OR liability)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm('')}
            autoComplete="off"
            isLoading={false}
            sx={{ width: '100%' }}
          />
        </Box>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ flex: 1, overflow: 'auto' }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 60, minWidth: 60 }}>
                  <Checkbox
                    indeterminate={
                      filteredSeeds.some((seed) => selectedIds.includes(seed._id)) &&
                      !filteredSeeds.every((seed) => selectedIds.includes(seed._id))
                    }
                    checked={
                      filteredSeeds.length > 0 &&
                      filteredSeeds.every((seed) => selectedIds.includes(seed._id))
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ width: 120, minWidth: 100 }}>Status</TableCell>
                <TableCell
                  sx={{
                    width: '40%',
                    minWidth: 200,
                    resize: 'horizontal',
                    overflow: 'auto',
                  }}
                >
                  Original Sentence
                </TableCell>
                <TableCell
                  sx={{
                    width: '40%',
                    minWidth: 200,
                    resize: 'horizontal',
                    overflow: 'auto',
                  }}
                >
                  Edited Sentence
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSeeds.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                      {searchTerm
                        ? 'No sentences match your search'
                        : 'No sentences available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSeeds.map((seed) => {
                  const isUsed = usedSeedIds.has(seed._id);
                  const isSelected = selectedIds.includes(seed._id);

                  return (
                    <TableRow
                      key={seed._id}
                      hover
                      onClick={() => handleToggle(seed._id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: isUsed ? 'action.hover' : 'inherit',
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected} />
                      </TableCell>
                      <TableCell>
                        {isUsed ? (
                          <Chip label="Used" size="small" icon={<CheckCircleIcon />} />
                        ) : (
                          <Chip label="Available" size="small" color="primary" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                        >
                          {seed.sentence}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                        >
                          {seed.edited_sentence || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={selectedIds.length === 0}
        >
          Use Selected Seeds ({selectedIds.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SeedSentenceSelector.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  availableSeeds: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      sentence: PropTypes.string.isRequired,
      edited_sentence: PropTypes.string,
    })
  ).isRequired,
  usedSeedIds: PropTypes.instanceOf(Set).isRequired,
};

export default SeedSentenceSelector;
